import { Send, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { sendToBot } from '../lib/telegram.js';
import { useAppStore } from '../store.js';
import { t } from '../lib/i18n.js';

function buildFocus({ productMode, data, careerData, language }) {
  if (productMode === 'career') {
    const activeCv = careerData?.cvManagement?.activeCv;
    const topTask = (careerData?.careerFeed || [])[0];
    if (!activeCv) {
      return {
        eyebrow: language === 'en' ? 'Start here' : 'Начать здесь',
        title: language === 'en' ? 'Upload your first CV' : 'Загрузите первое резюме',
        text: language === 'en'
          ? 'Careeva will parse your profile and unlock job matching, CV optimizer and career planner.'
          : 'Careeva распарсит профиль и включит подбор вакансий, оптимизатор CV и карьерный план.',
        action: 'career_cv_upload',
        label: language === 'en' ? 'Open CV' : 'Открыть CV'
      };
    }
    return {
      eyebrow: t(language, 'focusNow'),
      title: topTask?.title || (language === 'en' ? 'Career plan ready' : 'Карьерный план готов'),
      text: topTask?.nextBestAction || (language === 'en' ? 'Review today’s next best action.' : 'Проверь следующий лучший шаг на сегодня.'),
      action: topTask?.actions?.[0]?.action || 'career_review_jobs',
      label: topTask?.actions?.[0]?.label || (language === 'en' ? 'Open' : 'Открыть')
    };
  }

  const first = (data?.plannerItems || [])[0];
  if (!first || first.id === 'empty_day' || first.id === 'hire_bootstrap_create_vacancy') {
    return {
      eyebrow: language === 'en' ? 'Start here' : 'Начать здесь',
      title: language === 'en' ? 'Create your first vacancy' : 'Создайте первую вакансию',
      text: language === 'en'
        ? 'Hire OS will plan interviews, candidates and offers only after you add real hiring data.'
        : 'Hire OS начнёт планировать интервью, кандидатов и офферы только после реальных данных.',
      action: 'job_create',
      label: language === 'en' ? 'Open Hire OS' : 'Открыть Hire OS'
    };
  }
  return {
    eyebrow: t(language, 'focusNow'),
    title: first.title,
    text: first.nextBestAction || first.text,
    action: first.actions?.[0]?.action || 'refresh_plan',
    label: first.actions?.[0]?.label || t(language, 'openSchedule')
  };
}

export function Header({ user }) {
  const [query, setQuery] = useState('');
  const showToast = useAppStore((s) => s.showToast);
  const language = useAppStore((s) => s.language);
  const runCommand = useAppStore((s) => s.runCommand);
  const productMode = useAppStore((s) => s.productMode);
  const data = useAppStore((s) => s.data);
  const careerData = useAppStore((s) => s.careerData);
  const runAction = useAppStore((s) => s.runAction);
  const setView = useAppStore((s) => s.setView);
  const focus = buildFocus({ productMode, data, careerData, language });

  const submit = async () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    try {
      const apiResult = await runCommand(trimmed);
      const botResult = sendToBot('ai_command', { query: trimmed, api: { ok: apiResult?.ok, intent: apiResult?.intent?.intent } });
      showToast(apiResult?.message || (botResult.sentToTelegram ? (language === 'en' ? 'AI command sent to bot' : 'AI-команда отправлена в бот') : (language === 'en' ? 'AI command processed' : 'AI-команда обработана')));
    } catch (error) {
      showToast(language === 'en' ? 'Command failed' : 'Команда не выполнена');
    }
    setQuery('');
  };

  const openFocus = () => {
    if (focus.action === 'career_cv_upload') setView('cv');
    else runAction({ action: focus.action, label: focus.label });
  };

  return (
    <header className="top-shell">
      <section className="hero-card glass">
        <div className="hero-topline">
          <span className="status-dot" />
          <span>{new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'ru-RU', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date())}</span>
          <span className="shell-badge">Telegram-native</span>
        </div>
        <h1>{user?.first_name ? `${user.first_name}, ${t(language, 'todayPlan').toLowerCase()}` : t(language, 'todayPlan')}</h1>
        <p className="hero-copy">{t(language, 'heroCopy')}</p>
        <div className="command-bar" role="search">
          <Sparkles size={18} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} placeholder={t(language, 'askPlaceholder')} />
          <button className="icon-btn" aria-label="Отправить команду" onClick={submit}><Send size={17} /></button>
        </div>
      </section>
      <aside className="focus-card glass">
        <span className="eyebrow">{focus.eyebrow}</span>
        <h2>{focus.title}</h2>
        <p>{focus.text}</p>
        <button onClick={openFocus}>{focus.label}</button>
      </aside>
    </header>
  );
}
